/**
 * Optimisation du DOM pour réduire la complexité et améliorer les performances
 */

/**
 * Analyser la complexité du DOM
 */
export function analyzeDOMComplexity(): {
  totalNodes: number;
  maxDepth: number;
  largestElement: { tag: string; children: number } | null;
} {
  if (typeof document === 'undefined') {
    return { totalNodes: 0, maxDepth: 0, largestElement: null };
  }

  let totalNodes = 0;
  let maxDepth = 0;
  let largestElement: { tag: string; children: number } | null = null;
  let maxChildren = 0;

  function traverse(node: Node, depth: number): void {
    totalNodes++;
    maxDepth = Math.max(maxDepth, depth);

    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const childrenCount = element.children.length;

      if (childrenCount > maxChildren) {
        maxChildren = childrenCount;
        largestElement = {
          tag: element.tagName.toLowerCase(),
          children: childrenCount,
        };
      }

      Array.from(element.childNodes).forEach((child) => {
        traverse(child, depth + 1);
      });
    }
  }

  traverse(document.body, 0);

  return { totalNodes, maxDepth, largestElement };
}

/**
 * Nettoyer les nœuds DOM inutiles
 */
export function cleanupDOM(): void {
  if (typeof document === 'undefined') return;

  // Supprimer les commentaires HTML
  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_COMMENT,
    null
  );

  const commentsToRemove: Node[] = [];
  let node: Node | null;

  while ((node = walker.nextNode())) {
    commentsToRemove.push(node);
  }

  commentsToRemove.forEach((comment) => {
    comment.parentNode?.removeChild(comment);
  });

  // Supprimer les éléments vides inutiles
  const emptyElements = document.querySelectorAll('div:empty, span:empty, p:empty');
  emptyElements.forEach((el) => {
    if (!el.hasAttribute('data-keep') && el.children.length === 0) {
      el.remove();
    }
  });
}

/**
 * Optimiser les images pour réduire le CLS
 */
export function optimizeImagesForCLS(): void {
  if (typeof document === 'undefined') return;

  const images = document.querySelectorAll('img:not([width]):not([height])');

  images.forEach((img) => {
    const htmlImg = img as HTMLImageElement;

    // Si l'image est déjà chargée, définir les dimensions
    if (htmlImg.complete && htmlImg.naturalWidth > 0) {
      if (!htmlImg.hasAttribute('width')) {
        htmlImg.setAttribute('width', htmlImg.naturalWidth.toString());
      }
      if (!htmlImg.hasAttribute('height')) {
        htmlImg.setAttribute('height', htmlImg.naturalHeight.toString());
      }
    } else {
      // Sinon, attendre le chargement
      htmlImg.addEventListener('load', () => {
        if (!htmlImg.hasAttribute('width')) {
          htmlImg.setAttribute('width', htmlImg.naturalWidth.toString());
        }
        if (!htmlImg.hasAttribute('height')) {
          htmlImg.setAttribute('height', htmlImg.naturalHeight.toString());
        }
      });
    }
  });
}

/**
 * Réduire la profondeur du DOM en aplatissant les structures inutiles
 */
export function flattenDOM(): void {
  if (typeof document === 'undefined') return;

  // Trouver les divs avec un seul enfant div
  const unnecessaryWrappers = document.querySelectorAll('div > div:only-child');

  unnecessaryWrappers.forEach((wrapper) => {
    const parent = wrapper.parentElement;
    const child = wrapper as HTMLElement;

    // Vérifier que le wrapper n'a pas de classes ou styles importants
    if (
      parent &&
      !child.className &&
      !child.id &&
      !child.hasAttribute('style') &&
      !child.hasAttribute('data-keep')
    ) {
      // Déplacer tous les enfants du wrapper vers le parent
      while (child.firstChild) {
        parent.insertBefore(child.firstChild, child);
      }

      // Supprimer le wrapper
      child.remove();
    }
  });
}

/**
 * Initialiser toutes les optimisations DOM
 */
export function initDOMOptimizations(): void {
  if (typeof window === 'undefined') return;

  // Attendre que le DOM soit complètement chargé
  if (document.readyState === 'complete') {
    runOptimizations();
  } else {
    window.addEventListener('load', runOptimizations);
  }
}

function runOptimizations(): void {
  // Exécuter les optimisations
  cleanupDOM();
  optimizeImagesForCLS();
  flattenDOM();
}